import {
  Load_Create,
  Load_Read,
  Load_Read_Nested,
  Load_Update,
  LoadCycle_Read,
  LoadState,
  LoadSummary_Read_Nested,
  NullableOrderUnitOfMeasure,
  Pricing_Read,
  RateType,
  Waypoint_Create,
  Waypoint_Update_Destroyable,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { AiReview } from '~hooks/useAiReview/models';
import { MaterialRateAssignment } from '~hooks/useMaterialRates/models';
import { BasicMaterial } from '~hooks/useMaterials/models';
import { Pricing } from '~hooks/usePricing/models';
import { Phase } from '~hooks/useProjects/models';
import { WayPoint, WayPointFormProps } from '~hooks/useSites/models';
import { TicketBasic } from '~hooks/useTickets/models';
import { UserBasic } from '~hooks/useUsers/models';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';

/**
 * Spec gap: `Load_Read` does not yet carry the per-Load pricing fields the backend serializes under
 * `load_level_pricing`. Every field is undefined/null when the flag is OFF or the viewer cannot see
 * the given side's rate. The nested snapshots reuse the generated `Pricing_Read`.
 */
export type LoadReadWithPricing = Load_Read & {
  customer_rate_type?: RateType | null;
  customer_rate_value?: string | null;
  vendor_rate_type?: RateType | null;
  vendor_rate_value?: string | null;
  driver_rate_type?: RateType | null;
  driver_rate_value?: string | null;
  rate_inherited?: boolean;
  pricing?: Pricing_Read | null;
  driver_pricing?: Pricing_Read | null;
};

/**
 * Flag-gated (`load_level_pricing`) per-Load pricing fields, hand-extended until
 * they ship in `@treadinc/horizon-api-spec`. Every field is undefined/null when
 * the flag is OFF or the viewer cannot see the given side's rate.
 */
interface LoadPricingFields {
  customerRateType: Nullable<RateType>;
  customerRateValue: Nullable<number>;
  vendorRateType: Nullable<RateType>;
  vendorRateValue: Nullable<number>;
  driverRateType: Nullable<RateType>;
  driverRateValue: Nullable<number>;
  rateInherited: Nullable<boolean>;
  pricing: Nullable<Pricing>;
  driverPricing: Nullable<Pricing>;
}

const parseRateValue = (value: Nullable<string>): Nullable<number> =>
  value === null || value === undefined ? null : Number(value);

const parseLoadPricingFields = (proto: Load_Read): LoadPricingFields => {
  const extended = proto as LoadReadWithPricing;

  return {
    customerRateType: extended.customer_rate_type ?? null,
    customerRateValue: parseRateValue(extended.customer_rate_value),
    vendorRateType: extended.vendor_rate_type ?? null,
    vendorRateValue: parseRateValue(extended.vendor_rate_value),
    driverRateType: extended.driver_rate_type ?? null,
    driverRateValue: parseRateValue(extended.driver_rate_value),
    rateInherited: extended.rate_inherited ?? null,
    pricing: extended.pricing ? Pricing.parse(extended.pricing) : null,
    driverPricing: extended.driver_pricing
      ? Pricing.parse(extended.driver_pricing)
      : null,
  };
};

export const NON_CANCELLED_LOAD_STATES = [
  LoadState.CREATED,
  LoadState.TO_PICKUP,
  LoadState.ARRIVED_PICKUP,
  LoadState.LOADED,
  LoadState.TO_DROPOFF,
  LoadState.ARRIVED_DROPOFF,
  LoadState.UNLOADED,
  LoadState.COMPLETED,
];

export interface LoadDirtyWaypoints {
  pickup: boolean;
  dropoff: boolean;
  staging: boolean;
}

export interface LoadOptionsProps {
  quantity?: number | string;
  unitOfMeasure?: ItemNameAndId;
  id?: string;
  material?: Nullable<BasicMaterial>;
  waypoints?: WayPoint[];
  pickUpWaypoints?: WayPoint[];
  dropOffWaypoints?: WayPoint[];
  stagingWaypoints?: WayPoint[];
}

export class LoadCycle {
  public static parse(proto: LoadCycle_Read) {
    return new LoadCycle(proto.from_load_id, proto.to_load_id, proto.time_minutes);
  }

  get fromLoadId(): string {
    return this._from_load_id;
  }

  get toLoadId(): string {
    return this._to_load_id;
  }

  get timeMinutes(): number {
    return this._time_minutes || 0;
  }

  constructor(
    private _from_load_id: string,
    private _to_load_id: string,
    private _time_minutes: number,
  ) {}
}

export class LoadBasic {
  constructor(
    private _id: Nullable<string>,
    private _quantity: Nullable<string>,
    private _unit_of_measure: NullableOrderUnitOfMeasure,
    private _load_id: string,
    private _ordinality: number,
    private _ticket: Nullable<TicketBasic>,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get quantity() {
    return this._quantity;
  }

  public get unitOfMeasure() {
    return this._unit_of_measure;
  }

  public get loadId() {
    return this._load_id;
  }

  public get ordinality() {
    return this._ordinality;
  }

  public get ticket() {
    return this._ticket;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: Load_Read_Nested) {
    return new LoadBasic(
      proto.id,
      proto.quantity,
      proto.unit_of_measure,
      proto.load_id,
      proto.ordinality,
      proto.ticket ? TicketBasic.parse(proto.ticket) : null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class LoadDTO {
  public static parse(proto: Load_Read) {
    const pricingFields = parseLoadPricingFields(proto);

    return new LoadDTO(
      proto.id ?? '',
      proto.created_at ? dayjs(proto.created_at) : dayjs(),
      proto.updated_at ? dayjs(proto.updated_at) : dayjs(),
      proto.ordinality || 0,
      proto.quantity ?? '',
      proto.unit_of_measure
        ? ItemNameAndId.parse({ name: proto.unit_of_measure, id: proto.unit_of_measure })
        : null,
      proto.state ?? LoadState.CREATED,
      proto.approvable,
      proto.approved,
      proto.unapprovable,
      proto.waypoints.map(WayPoint.parse),
      proto.foremen.map((foreman) => UserBasic.parse(foreman)),
      proto.load_id || '',
      proto.job_id || '',
      proto.ticket ? TicketBasic.parse(proto.ticket) : undefined,
      proto.material ? BasicMaterial.parse(proto.material) : null,
      proto.field_approved_at ? dayjs(proto.field_approved_at) : null,
      proto.billing_approved_at ? dayjs(proto.billing_approved_at) : null,
      proto.ai_review ? AiReview.parse(proto.ai_review) : null,
      pricingFields.customerRateType,
      pricingFields.customerRateValue,
      pricingFields.vendorRateType,
      pricingFields.vendorRateValue,
      pricingFields.driverRateType,
      pricingFields.driverRateValue,
      pricingFields.rateInherited,
      pricingFields.pricing,
      pricingFields.driverPricing,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public static deparse(proto: LoadOptionsProps): Load_Create {
    const pickUpSiteWaypoint: Nullable<Waypoint_Create> = proto.pickUpWaypoints?.[0]?.site
      ?.id
      ? WayPoint.deparse({
          id: proto.pickUpWaypoints[0].id,
          site: proto.pickUpWaypoints[0].site,
          ordinality: 10,
          type: WaypointType.PICKUP,
          contact: proto.pickUpWaypoints[0].contact,
        } as WayPointFormProps)
      : null;

    const stagingSiteWaypoint: Nullable<Waypoint_Create> = proto.stagingWaypoints?.[0]
      ?.site?.id
      ? WayPoint.deparse({
          id: proto.stagingWaypoints[0].id,
          site: proto.stagingWaypoints[0].site,
          ordinality: 15,
          type: WaypointType.STAGING,
          contact: proto.stagingWaypoints[0].contact,
        } as WayPointFormProps)
      : null;

    const dropOffSiteWaypoint: Nullable<Waypoint_Create> = proto.dropOffWaypoints?.[0]
      ?.site?.id
      ? WayPoint.deparse({
          id: proto.dropOffWaypoints[0].id,
          site: proto.dropOffWaypoints[0].site,
          ordinality: 20,
          type: WaypointType.DROP_OFF,
          contact: proto.dropOffWaypoints[0].contact,
        } as WayPointFormProps)
      : null;

    const waypoints: Array<Waypoint_Create> | undefined = [
      pickUpSiteWaypoint,
      stagingSiteWaypoint,
      dropOffSiteWaypoint,
    ].filter((waypoint): waypoint is Waypoint_Create => waypoint !== null);

    return {
      material_id: proto.material?.id || null,
      waypoints: waypoints.length ? waypoints : undefined,
    };
  }

  public static deparseUpdate(
    proto: Load,
    originalWaypoints: WayPoint[],
    dirtyWaypoints: LoadDirtyWaypoints,
  ): Load_Update {
    const originalPickUpWaypointId = originalWaypoints.find(
      (waypoint) => waypoint.type === WaypointType.PICKUP,
    )?.id;
    const originalDropOffWaypointId = originalWaypoints.find(
      (waypoint) => waypoint.type === WaypointType.DROP_OFF,
    )?.id;
    const originalStagingWaypointId = originalWaypoints.find(
      (waypoint) => waypoint.type === WaypointType.STAGING,
    )?.id;

    const pickUpSiteWaypoint: Nullable<Waypoint_Create> =
      dirtyWaypoints.pickup && proto.pickUpWaypoints?.[0]?.site?.id
        ? WayPoint.deparse(
            {
              id: originalPickUpWaypointId || '',
              site: proto.pickUpWaypoints[0].site,
              ordinality: 10,
              type: WaypointType.PICKUP,
              contact: proto.pickUpWaypoints[0].contact ?? undefined,
            } as WayPointFormProps,
            false,
          )
        : null;

    const stagingSiteWaypoint: Nullable<Waypoint_Create> =
      dirtyWaypoints.staging && proto.stagingWaypoints?.[0]?.site?.id
        ? WayPoint.deparse(
            {
              id: originalStagingWaypointId || '',
              site: proto.stagingWaypoints[0].site,
              ordinality: 15,
              type: WaypointType.STAGING,
              contact: proto.stagingWaypoints[0].contact ?? undefined,
            } as WayPointFormProps,
            false,
          )
        : null;

    const dropOffSiteWaypoint: Nullable<Waypoint_Create> =
      dirtyWaypoints.dropoff && proto.dropOffWaypoints?.[0]?.site?.id
        ? WayPoint.deparse(
            {
              id: originalDropOffWaypointId || '',
              site: proto.dropOffWaypoints[0].site,
              ordinality: 20,
              type: WaypointType.DROP_OFF,
              contact: proto.dropOffWaypoints[0].contact ?? undefined,
            } as WayPointFormProps,
            false,
          )
        : null;

    // Only emit a waypoint entry (create/update or _destroy) for types the user
    // actually edited. Untouched types must be omitted entirely — otherwise the
    // stale FE form state races against the BE's job→load waypoint cascade.
    const waypoints: Array<Waypoint_Update_Destroyable> | undefined = [
      ...(dirtyWaypoints.pickup
        ? pickUpSiteWaypoint
          ? [pickUpSiteWaypoint]
          : originalPickUpWaypointId
            ? [{ id: originalPickUpWaypointId, _destroy: 1 }]
            : []
        : []),
      ...(dirtyWaypoints.staging
        ? stagingSiteWaypoint
          ? [stagingSiteWaypoint]
          : originalStagingWaypointId
            ? [{ id: originalStagingWaypointId, _destroy: 1 }]
            : []
        : []),
      ...(dirtyWaypoints.dropoff
        ? dropOffSiteWaypoint
          ? [dropOffSiteWaypoint]
          : originalDropOffWaypointId
            ? [{ id: originalDropOffWaypointId, _destroy: 1 }]
            : []
        : []),
    ];

    const body: Load_Update = {
      quantity: String(proto.quantity),
      unit_of_measure: proto.unitOfMeasure
        ? (proto.unitOfMeasure.id as NullableOrderUnitOfMeasure)
        : null,
      material_id: proto.material?.id || null,
      waypoints: waypoints.length ? waypoints : undefined,
    };

    return body;
  }

  public get id(): string {
    return this._id;
  }

  public get createdAt(): Dayjs {
    return this._created_at;
  }

  public get updatedAt(): Dayjs {
    return this._updated_at;
  }

  public get ordinality(): number {
    return this._ordinality;
  }

  public get quantity(): string {
    return this._quantity;
  }

  public set quantity(value: string) {
    this._quantity = value;
  }

  public get unitOfMeasure(): Nullable<ItemNameAndId> {
    return this._unit_of_measure;
  }

  public set unitOfMeasure(value: Nullable<ItemNameAndId>) {
    this._unit_of_measure = ItemNameAndId.parse(value);
  }

  public get status(): LoadState {
    return this._status;
  }

  public get loadId(): string {
    return this._load_id || '';
  }

  public get jobId(): string {
    return this._job_id || '';
  }

  public get ticket() {
    return this._ticket;
  }

  public get foremen() {
    return this._foremen;
  }

  public get approvable() {
    return Boolean(this._approvable);
  }

  public get approved() {
    return Boolean(this._approved);
  }

  public get unapprovable() {
    return Boolean(this._unapprovable);
  }

  public get material() {
    return this._material;
  }

  public get waypoints() {
    return this._waypoints;
  }

  public get fieldApprovedAt() {
    return this._field_approved_at;
  }

  public get billingApprovedAt() {
    return this._billing_approved_at;
  }

  public get aiReview() {
    return this._ai_review;
  }

  public get customerRateType() {
    return this._customer_rate_type ?? null;
  }

  public get customerRateValue() {
    return this._customer_rate_value ?? null;
  }

  public get vendorRateType() {
    return this._vendor_rate_type ?? null;
  }

  public get vendorRateValue() {
    return this._vendor_rate_value ?? null;
  }

  public get driverRateType() {
    return this._driver_rate_type ?? null;
  }

  public get driverRateValue() {
    return this._driver_rate_value ?? null;
  }

  /**
   * True ⟺ the Load inherits pricing from its hop, false ⟺ it has divergent
   * (overridden) pricing. Null when the `load_level_pricing` flag is OFF.
   */
  public get rateInherited() {
    return this._rate_inherited ?? null;
  }

  public get pricing() {
    return this._pricing ?? null;
  }

  public get driverPricing() {
    return this._driver_pricing ?? null;
  }

  public get discardedAt() {
    return this._discarded_at ?? null;
  }

  constructor(
    private _id: string,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _ordinality: number,
    private _quantity: string,
    private _unit_of_measure: Nullable<ItemNameAndId>,
    private _status: LoadState,
    private _approvable: boolean,
    private _approved: boolean,
    private _unapprovable: boolean,
    private _waypoints: WayPoint[],
    private _foremen: UserBasic[],
    private _load_id?: string,
    private _job_id?: string,
    private _ticket?: TicketBasic,
    private _material?: Nullable<BasicMaterial>,
    private _field_approved_at?: Nullable<Dayjs>,
    private _billing_approved_at?: Nullable<Dayjs>,
    private _ai_review?: Nullable<AiReview>,
    private _customer_rate_type?: Nullable<RateType>,
    private _customer_rate_value?: Nullable<number>,
    private _vendor_rate_type?: Nullable<RateType>,
    private _vendor_rate_value?: Nullable<number>,
    private _driver_rate_type?: Nullable<RateType>,
    private _driver_rate_value?: Nullable<number>,
    private _rate_inherited?: Nullable<boolean>,
    private _pricing?: Nullable<Pricing>,
    private _driver_pricing?: Nullable<Pricing>,
    private _discarded_at: Dayjs | null = null,
  ) {}
}

export class Load {
  constructor(
    private _id: string,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _quantity: number,
    private _unit_of_measure: ItemNameAndId,
    private _load_id: string,
    private _ordinality: number,
    private _ticket: Nullable<TicketBasic>,
    private _foremen: UserBasic[],
    private _job_id: string,
    private _state: LoadState,
    private _load_summary: Nullable<LoadSummaryBasic>,
    private _approvable: boolean,
    private _approved: boolean,
    private _unapprovable: boolean,
    private _material: Nullable<BasicMaterial>,
    private _waypoints: WayPoint[],
    private _pick_up_waypoints: WayPoint[],
    private _drop_off_waypoints: WayPoint[],
    private _staging_waypoints: WayPoint[],
    private _notes: Nullable<string>,
    private _approved_at: Nullable<Dayjs>,
    private _billing_approved_at: Nullable<Dayjs>,
    private _field_approved_at: Nullable<Dayjs>,
    private _ai_review: Nullable<AiReview>,
    private _phase: Nullable<Phase>,
    private _material_rate_assignment: Nullable<MaterialRateAssignment>,
    private _customer_rate_type?: Nullable<RateType>,
    private _customer_rate_value?: Nullable<number>,
    private _vendor_rate_type?: Nullable<RateType>,
    private _vendor_rate_value?: Nullable<number>,
    private _driver_rate_type?: Nullable<RateType>,
    private _driver_rate_value?: Nullable<number>,
    private _rate_inherited?: Nullable<boolean>,
    private _pricing?: Nullable<Pricing>,
    private _driver_pricing?: Nullable<Pricing>,
    private _discarded_at: Dayjs | null = null,
    private _editable: Nullable<boolean> = null,
  ) {}

  public static parse(proto: Load_Read) {
    const waypoints = proto.waypoints.map(WayPoint.parse);
    const pricingFields = parseLoadPricingFields(proto);

    return new Load(
      proto.id,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      Number(proto.quantity),
      ItemNameAndId.parse({ id: proto.unit_of_measure, name: proto.unit_of_measure }),
      proto.load_id,
      proto.ordinality,
      proto.ticket ? TicketBasic.parse(proto.ticket) : null,
      proto.foremen.map((foreman) => UserBasic.parse(foreman)),
      proto.job_id,
      proto.state,
      proto.load_summary ? LoadSummaryBasic.parse(proto.load_summary) : null,
      proto.approvable,
      proto.approved,
      proto.unapprovable,
      proto.material ? BasicMaterial.parse(proto.material) : null,
      waypoints,
      waypoints.filter((waypoint) => waypoint.type === WaypointType.PICKUP),
      waypoints.filter((waypoint) => waypoint.type === WaypointType.DROP_OFF),
      waypoints.filter((waypoint) => waypoint.type === WaypointType.STAGING),
      proto.notes ?? null,
      proto.approved_at ? dayjs(proto.approved_at) : null,
      proto.billing_approved_at ? dayjs(proto.billing_approved_at) : null,
      proto.field_approved_at ? dayjs(proto.field_approved_at) : null,
      proto.ai_review ? AiReview.parse(proto.ai_review) : null,
      proto.phase ? Phase.parse(proto.phase) : null,
      proto.material_rate_assignment
        ? MaterialRateAssignment.parse(proto.material_rate_assignment)
        : null,
      pricingFields.customerRateType,
      pricingFields.customerRateValue,
      pricingFields.vendorRateType,
      pricingFields.vendorRateValue,
      pricingFields.driverRateType,
      pricingFields.driverRateValue,
      pricingFields.rateInherited,
      pricingFields.pricing,
      pricingFields.driverPricing,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.editable,
    );
  }

  public get id() {
    return this._id;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get quantity(): number {
    return this._quantity;
  }

  public set quantity(value: string) {
    this._quantity = Number(value);
  }

  public get unitOfMeasure() {
    return this._unit_of_measure;
  }

  public set unitOfMeasure(value: ItemNameAndId) {
    this._unit_of_measure = value;
  }

  public get loadId() {
    return this._load_id;
  }

  public get ordinality() {
    return this._ordinality;
  }

  public get ticket() {
    return this._ticket;
  }

  public get foremen() {
    return this._foremen;
  }

  public get jobId() {
    return this._job_id;
  }

  public get state() {
    return this._state;
  }

  public get loadSummary() {
    return this._load_summary;
  }

  public set loadSummary(value: Nullable<LoadSummaryBasic>) {
    this._load_summary = value;
  }

  public get approvable() {
    return this._approvable;
  }

  public get approved() {
    return this._approved;
  }

  public get unapprovable() {
    return this._unapprovable;
  }

  public get material() {
    return this._material;
  }

  public set material(value: Nullable<BasicMaterial>) {
    this._material = value;
  }

  public get waypoints() {
    return this._waypoints;
  }

  public set waypoints(value: WayPoint[]) {
    this._waypoints = value;
  }

  public get pickUpWaypoints() {
    return this._pick_up_waypoints;
  }

  public set pickUpWaypoints(value: WayPoint[]) {
    this._pick_up_waypoints = value;
  }

  public get dropOffWaypoints() {
    return this._drop_off_waypoints;
  }

  public set dropOffWaypoints(value: WayPoint[]) {
    this._drop_off_waypoints = value;
  }

  public get stagingWaypoints() {
    return this._staging_waypoints;
  }

  public set stagingWaypoints(value: WayPoint[]) {
    this._staging_waypoints = value;
  }

  public get notes() {
    return this._notes;
  }

  public set notes(value: Nullable<string>) {
    this._notes = value;
  }

  public get approvedAt() {
    return this._approved_at;
  }

  public get billingApprovedAt() {
    return this._billing_approved_at;
  }

  public get fieldApprovedAt() {
    return this._field_approved_at;
  }

  public get aiReview() {
    return this._ai_review;
  }

  public get phase() {
    return this._phase;
  }

  public get materialRateAssignment() {
    return this._material_rate_assignment;
  }

  public get customerRateType() {
    return this._customer_rate_type ?? null;
  }

  public get customerRateValue() {
    return this._customer_rate_value ?? null;
  }

  public get vendorRateType() {
    return this._vendor_rate_type ?? null;
  }

  public get vendorRateValue() {
    return this._vendor_rate_value ?? null;
  }

  public get driverRateType() {
    return this._driver_rate_type ?? null;
  }

  public get driverRateValue() {
    return this._driver_rate_value ?? null;
  }

  /**
   * True ⟺ the Load inherits pricing from its hop, false ⟺ it has divergent
   * (overridden) pricing. Null when the `load_level_pricing` flag is OFF.
   */
  public get rateInherited() {
    return this._rate_inherited ?? null;
  }

  public get pricing() {
    return this._pricing ?? null;
  }

  public get driverPricing() {
    return this._driver_pricing ?? null;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public get editable() {
    return this._editable;
  }
}

export class LoadSummaryBasic {
  constructor(
    private _arrived_pickup_at: Nullable<Dayjs>,
    private _pickup_time_minutes: number,
    private _arrived_dropoff_at: Nullable<Dayjs>,
    private _dropoff_time_minutes: number,
    private _load_completed_at: Nullable<Dayjs>,
    private _loaded_at: Nullable<Dayjs>,
    private _unloaded_at: Nullable<Dayjs>,
    private _gps_minutes: number,
    private _has_gps_data: boolean,
    private _travelled_distance_meters: string | null,
    private _idle_time_seconds: number,
  ) {}

  public static parse(proto: LoadSummary_Read_Nested) {
    return new LoadSummaryBasic(
      proto.arrived_pickup_at ? dayjs(proto.arrived_pickup_at) : null,
      proto.pickup_time_minutes,
      proto.arrived_dropoff_at ? dayjs(proto.arrived_dropoff_at) : null,
      proto.dropoff_time_minutes,
      proto.load_completed_at ? dayjs(proto.load_completed_at) : null,
      proto.loaded_at ? dayjs(proto.loaded_at) : null,
      proto.unloaded_at ? dayjs(proto.unloaded_at) : null,
      proto.gps_minutes,
      proto.has_gps_data || false,
      proto.travelled_distance_meters ?? null,
      proto.idle_time_seconds,
    );
  }

  public get arrivedPickupAt() {
    return this._arrived_pickup_at;
  }

  public get pickupTimeMinutes() {
    return this._pickup_time_minutes;
  }

  public get arrivedDropoffAt() {
    return this._arrived_dropoff_at;
  }

  public get dropoffTimeMinutes() {
    return this._dropoff_time_minutes;
  }

  public get loadCompletedAt() {
    return this._load_completed_at;
  }

  public get loadedAt() {
    return this._loaded_at;
  }

  public get unloadedAt() {
    return this._unloaded_at;
  }

  public get gpsMinutes() {
    return this._gps_minutes;
  }

  public get hasGpsData() {
    return this._has_gps_data;
  }

  public get travelledDistanceMeters() {
    return this._travelled_distance_meters;
  }

  public get idleTimeSeconds() {
    return this._idle_time_seconds;
  }
}
